import { useState } from 'react';
import { Button } from '../../ui/button';
import { Download, FileText, RefreshCw } from 'lucide-react';
import { useGenerateBatchPDFs } from '@/hooks/react-query/useDrawdownBatches';
import { DrawdownBatchService } from '@/services/drawdowns/drawdownBatchService';
import type { BatchInvestorNotice } from '@/types/drawdown';

interface DrawdownBatchActionsProps {
  investorNotices: BatchInvestorNotice[];
  fundId: string;
  drawdownId: string;
  batchId: string;
  docsGenerating?: boolean;
}

export function DrawdownBatchActions({
  investorNotices,
  fundId,
  drawdownId,
  batchId,
  docsGenerating = false,
}: DrawdownBatchActionsProps) {
  const [isDownloading, setIsDownloading] = useState(false);

  const generateBatchPDFsMutation = useGenerateBatchPDFs();

  const pdfsGenerated = investorNotices.filter(
    (inv) => inv.pdf_generated
  ).length;

  const handleGenerateAllPDFs = async () => {
    try {
      await generateBatchPDFsMutation.mutateAsync({
        fundId,
        drawdownId,
        batchId,
        regenerate: false,
      });
    } catch (error) {
      console.error('Failed to generate PDFs:', error);
    }
  };

  const handleDownloadAll = async () => {
    try {
      setIsDownloading(true);
      await DrawdownBatchService.downloadBatchPdfsZip(
        fundId,
        drawdownId,
        batchId
      );
    } catch (error) {
      console.error('Failed to download PDFs:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {docsGenerating && (
        <Button variant="outline" size="sm" disabled>
          <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          Generating...
        </Button>
      )}
      {!docsGenerating && pdfsGenerated === 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleGenerateAllPDFs}
          disabled={
            investorNotices.length === 0 || generateBatchPDFsMutation.isPending
          }
        >
          {generateBatchPDFsMutation.isPending ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <FileText className="h-4 w-4 mr-2" />
          )}
          {generateBatchPDFsMutation.isPending
            ? 'Generating...'
            : 'Generate PDFs'}
        </Button>
      )}
      {!docsGenerating && pdfsGenerated > 0 && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleDownloadAll}
          disabled={isDownloading}
        >
          {isDownloading ? (
            <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Download className="h-4 w-4 mr-2" />
          )}
          {isDownloading ? 'Downloading...' : 'Download PDFs'}
        </Button>
      )}
    </div>
  );
}
